import { Box, Grid, List, Paper, Typography, useTheme } from "@mui/material";
import { GetFieldLookupItem, useFormMetadata, useLookups } from "@ngt/forms";
import { DateTime } from "luxon";
import { useContext, useMemo } from "react";
import { Link, Outlet, useNavigate, useParams } from "react-router-dom";
import { ContractSummary, FormDefinitions, LotNote, Permissions } from "../api/dtos";
import {
    DetailsContent,
    DetailsHeader,
    DetailsPanel,
    GridColumn,
    GridData,
    GridFillerRow,
    GridLabel,
    GridRow,
} from "../components/DetailsPanel";
import GridActions from "../components/GridActions";
import LotAttachments from "../components/LotAttachments";
import { LotHistory } from "../components/LotHistory";
import { StyledActionButton, StyledButton } from "../components/StyledButton";
import CollapsibleAppTable from "../components/tables/CollapsibleAppTable";
import type { AppColumn, AppScalarValue } from "../components/tables/AppTable.types";
import TitleBox from "../components/TitleBox";
import PermissionsContext from "../contexts/auth/PermissionsContext";
import LotContext from "../contexts/data/LotContext";
import ProjectContext from "../contexts/data/ProjectContext";
import StageContext from "../contexts/data/StageContext";
import LotAttachmentsContext from "../contexts/LotAttachmentsContext";
import LotChangesContext from "../contexts/LotChangesContext";
import LotDetailsContext from "../contexts/LotDetailsContext";
import LotNotesContext from "../contexts/LotNotesContext";
import LotPropertiesContext from "../contexts/LotPropertiesContext";
import useAttachmentsByLotId from "../hooks/data/useAttachmentsByLotId";
import useContractSummariesByLotId from "../hooks/data/useContractSummariesByLotId";
import useLotChangesByLotId from "../hooks/data/useLotChangesByLotId";
import useLotDetailsByProjectIdStageIdLotId from "../hooks/data/useLotDetailsByProjectIdStageIdLotId";
import useLotNotesByLotId from "../hooks/data/useLotNotesByLotId";
import ContractSummariesContext from "../contexts/ContractSummariesContext";

const audFormatter = new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
});
const formatAUD = (value: number) => audFormatter.format(value);
const localCalendarDate = (value?: string) => {
    if (value === undefined) {
        return undefined;
    }

    const date = DateTime.fromISO(value);

    return date.isValid ? (date.toISODate() ?? value) : value;
};

function requiredContractId(row: ContractSummary): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Contract summary is missing its contract ID.");
    }

    return row.id;
}

function requiredLotNoteId(row: LotNote): number {
    if (row.id === undefined || row.id === null) {
        throw new Error("Lot note is missing its note ID.");
    }

    return row.id;
}

export function createContractColumns(
    permissions: readonly Permissions[] | undefined,
    projectId: string | undefined,
    stageId: string | undefined,
    lotId: string | undefined,
    statusLabel: (value: AppScalarValue) => string,
): AppColumn<ContractSummary>[] {
    const currencyColumn = (
        id: "price" | "rateAdjustments" | "legalFees" | "commission" | "halfShareInterest",
        label: string,
    ): AppColumn<ContractSummary> => ({
        id,
        label,
        scalarType: "number",
        accessor: (row) => row[id],
        align: "left",
        width: 150,
        aggregate: "sum",
        formatter: (value) => (typeof value === "number" ? formatAUD(value) : ""),
        aggregateFormatter: formatAUD,
    });
    const columns: AppColumn<ContractSummary>[] = [
        {
            id: "status",
            label: "Contract Status",
            scalarType: "lookup",
            accessor: (row) => row.status,
            lookup: {
                labelFor: (value) => statusLabel(value),
            },
            sortAccessor: (row) => statusLabel(row.status),
            sortScalarType: "text",
            width: 180,
        },
        currencyColumn("price", "Contract Price"),
        {
            id: "exchangeDate",
            label: "Exchange Date",
            scalarType: "date",
            accessor: (row) => localCalendarDate(row.exchangeDate),
            formatter: (value) =>
                typeof value === "string" && DateTime.fromISO(value).isValid
                    ? DateTime.fromISO(value).toFormat("dd/MM/yyyy")
                    : "",
            width: 150,
        },
        currencyColumn("rateAdjustments", "Rate Adjustments"),
        currencyColumn("legalFees", "Legal Fees"),
        currencyColumn("commission", "Commission"),
        currencyColumn("halfShareInterest", "Half Share Interest"),
        {
            id: "agentName",
            label: "Agent",
            scalarType: "text",
            accessor: (row) => row.agentName,
            minWidth: 100,
            flex: 1,
        },
        {
            id: "settlementDate",
            label: "Settlement Date",
            scalarType: "date",
            accessor: (row) => localCalendarDate(row.settlementDate),
            formatter: (value) =>
                typeof value === "string" && DateTime.fromISO(value).isValid
                    ? DateTime.fromISO(value).toFormat("dd/MM/yyyy")
                    : "",
            width: 150,
        },
    ];

    if (
        permissions?.includes(Permissions.LotCanEditContract) ||
        permissions?.includes(Permissions.LotCanDeleteContract)
    ) {
        columns.push({
            id: "actions",
            label: "",
            headerLabel: "",
            kind: "action",
            scalarType: "text",
            accessor: (row) => String(row.id ?? ""),
            width: 100,
            cell: ({ row }) => (
                <GridActions
                    editPermission={Permissions.LotCanEditContract}
                    editUrl={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/contracts/edit/${requiredContractId(row)}`}
                />
            ),
        });
    }

    return columns;
}

const useContractColumns = () => {
    const metadata = useFormMetadata(FormDefinitions.Contract);
    const routeParams = useParams();

    const { data: permissions } = useContext(PermissionsContext);

    const { data: lookups, loading: _lookupsLoading } = useLookups(metadata);

    const projectId = routeParams.projectId;
    const stageId = routeParams.stageId;
    const lotId = routeParams.lotId;

    const contractColumns = useMemo(
        () =>
            createContractColumns(permissions, projectId, stageId, lotId, (value) =>
                typeof value === "number"
                    ? (GetFieldLookupItem(lookups, "Status", value)?.value?.toString() ?? String(value))
                    : String(value ?? ""),
            ),
        [lookups, permissions, projectId, stageId, lotId],
    );

    return contractColumns;
};

export function createLotNoteColumns(
    permissions: readonly Permissions[] | undefined,
    projectId: string | undefined,
    stageId: string | undefined,
    lotId: string | undefined,
): AppColumn<LotNote>[] {
    const columns: AppColumn<LotNote>[] = [
        {
            id: "noteText",
            label: "Note Text",
            scalarType: "text",
            accessor: (row) => row.noteText,
            minWidth: 100,
            flex: 1,
        },
        {
            id: "enteredBy",
            label: "Entered By",
            scalarType: "text",
            accessor: (row) => row.enteredBy,
            width: 200,
        },
    ];

    if (permissions?.includes(Permissions.LotCanEditNote) || permissions?.includes(Permissions.LotCanDeleteNote)) {
        columns.push({
            id: "actions",
            label: "",
            headerLabel: "",
            kind: "action",
            scalarType: "text",
            accessor: (row) => String(row.id ?? ""),
            width: 100,
            cell: ({ row }) => (
                <GridActions
                    editPermission={Permissions.LotCanEditNote}
                    deletePermission={Permissions.LotCanDeleteNote}
                    editUrl={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/notes/edit/${requiredLotNoteId(row)}`}
                    deleteUrl={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/notes/delete/${requiredLotNoteId(row)}`}
                />
            ),
        });
    }

    return columns;
}

const useLotNoteColumns = () => {
    const { data: permissions } = useContext(PermissionsContext);
    const routeParams = useParams();

    const projectId = routeParams.projectId;
    const stageId = routeParams.stageId;
    const lotId = routeParams.lotId;

    const lotNoteColumns = useMemo(
        () => createLotNoteColumns(permissions, projectId, stageId, lotId),
        [permissions, projectId, stageId, lotId],
    );

    return lotNoteColumns;
};

export const LotDetails = () => {
    const lotMetadata = useFormMetadata(FormDefinitions.Lot);

    const { data: permissions } = useContext(PermissionsContext);

    const { data: lotLookups, loading: _lookupsLoading } = useLookups(lotMetadata);

    const theme = useTheme();
    const navigate = useNavigate();

    const routeParams = useParams();

    const contractColumns = useContractColumns();
    const lotNoteColumns = useLotNoteColumns();

    const projectId = Number(routeParams.projectId);
    const stageId = Number(routeParams.stageId);
    const lotId = Number(routeParams.lotId);

    if (!lotId) {
        navigate(`/projects/${projectId}/stages/${stageId}`);
    }

    const { data: project } = useContext(ProjectContext);
    const { data: stage } = useContext(StageContext);
    const { data: lot } = useContext(LotContext);

    const lotDetailsContextData = useLotDetailsByProjectIdStageIdLotId(projectId, stageId, lotId);
    const { data: lotData, loading: _lotLoading, refresh: refreshLots } = lotDetailsContextData;

    const lotPropertiesContextData = useContext(LotPropertiesContext);

    const contractSummariesContextData = useContractSummariesByLotId(lotId);
    const { data: contractSummaries, loading: contractSummariesLoading } = contractSummariesContextData;

    const lotNotesContextData = useLotNotesByLotId(lotId);
    const { data: lotNotes, loading: lotNotesLoading } = lotNotesContextData;

    const lotAttachmentsContextData = useAttachmentsByLotId(lotId);

    const lotChangesContextData = useLotChangesByLotId(lotId);

    const contractsExport = useMemo(
        () => ({
            exportTitle: `${project?.name} - ${stage?.name} - Lot ${lot?.number}`,
            fileName: `${project?.name} - ${stage?.name} - Lot ${lot?.number}`,
            includeDateTime: true,
        }),
        [project, stage, lot],
    );

    const notesExport = useMemo(
        () => ({
            exportTitle: `${project?.name} - ${stage?.name} - Lot ${lot?.number} - Notes`,
            fileName: `${project?.name} - ${stage?.name} - Lot ${lot?.number} - Notes`,
            includeDateTime: true,
        }),
        [project, stage, lot],
    );

    return (
        <>
            <LotDetailsContext.Provider value={lotDetailsContextData}>
                <LotChangesContext.Provider value={lotChangesContextData}>
                    <LotPropertiesContext.Provider
                        value={{
                            ...lotPropertiesContextData,
                            refresh: refreshLots,
                        }}
                    >
                        <LotNotesContext.Provider value={lotNotesContextData}>
                            <LotAttachmentsContext.Provider value={lotAttachmentsContextData}>
                                <ContractSummariesContext.Provider value={contractSummariesContextData}>
                                    <Outlet />

                                    <TitleBox>
                                        <Typography variant="h2">
                                            {lotData?.projectName ? `${lotData?.projectName} - ` : undefined}
                                            {lotData?.stageName ? `${lotData?.stageName} - ` : undefined}
                                            {lotData?.lotNo ? `Lot ${lotData?.lotNo}` : undefined}
                                        </Typography>
                                        <Grid container gap={theme.spacing(2)} justifyContent={"end"}>
                                            {permissions?.includes(Permissions.StageCanEditLot) && (
                                                <StyledButton
                                                    variant="contained"
                                                    //@ts-expect-error
                                                    component={Link}
                                                    to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/edit`}
                                                    state={{
                                                        back: true,
                                                    }}
                                                >
                                                    Edit Lot
                                                </StyledButton>
                                            )}
                                            {permissions?.includes(Permissions.LotCanAddContract) && (
                                                <StyledButton
                                                    variant="contained"
                                                    //@ts-expect-error
                                                    component={Link}
                                                    to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/contracts/create`}
                                                    state={{
                                                        back: true,
                                                    }}
                                                >
                                                    Add Contract
                                                </StyledButton>
                                            )}
                                            {permissions?.includes(Permissions.LotCanAddProperty) && (
                                                <StyledButton
                                                    variant="contained"
                                                    //@ts-expect-error
                                                    component={Link}
                                                    to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/properties/create`}
                                                    state={{
                                                        back: true,
                                                    }}
                                                >
                                                    Add Lot Property
                                                </StyledButton>
                                            )}
                                            {permissions?.includes(Permissions.LotCanAddNote) && (
                                                <StyledButton
                                                    variant="contained"
                                                    //@ts-expect-error
                                                    component={Link}
                                                    to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/notes/create`}
                                                    state={{
                                                        back: true,
                                                    }}
                                                >
                                                    Add Note
                                                </StyledButton>
                                            )}
                                            {permissions?.includes(Permissions.LotCanAddAttachment) && (
                                                <StyledButton
                                                    variant="contained"
                                                    //@ts-expect-error
                                                    component={Link}
                                                    to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/attachments/create`}
                                                    state={{
                                                        back: true,
                                                    }}
                                                >
                                                    Add Attachment
                                                </StyledButton>
                                            )}
                                        </Grid>
                                    </TitleBox>

                                    <Grid
                                        container
                                        size={{
                                            xs: 12,
                                        }}
                                        spacing={{
                                            xs: 2,
                                        }}
                                    >
                                        <Grid
                                            size={{
                                                xs: 12,
                                                lg: 6,
                                            }}
                                            sx={{
                                                display: "flex",
                                                alignItems: "stretch",
                                            }}
                                        >
                                            <DetailsPanel>
                                                <DetailsHeader>Summary</DetailsHeader>
                                                <DetailsContent>
                                                    <GridColumn>
                                                        <GridRow>
                                                            <GridLabel>Status:</GridLabel>
                                                            <GridData>
                                                                {GetFieldLookupItem(
                                                                    lotLookups,
                                                                    "Status",
                                                                    lotData?.status,
                                                                )?.value?.toString()}
                                                            </GridData>
                                                        </GridRow>
                                                        <GridRow>
                                                            <GridLabel>Price:</GridLabel>
                                                            <GridData>
                                                                {lotData?.price
                                                                    ? Intl.NumberFormat("en-AU", {
                                                                          style: "currency",
                                                                          currency: "AUD",
                                                                      }).format(lotData?.price)
                                                                    : undefined}
                                                            </GridData>
                                                        </GridRow>
                                                        <GridRow>
                                                            <GridLabel>Release Date:</GridLabel>
                                                            <GridData>
                                                                {lotData?.releaseDate
                                                                    ? DateTime.fromISO(
                                                                          lotData.releaseDate.toString(),
                                                                      ).toFormat("dd/MM/yyyy")
                                                                    : ""}
                                                            </GridData>
                                                        </GridRow>
                                                        <GridFillerRow />
                                                    </GridColumn>
                                                </DetailsContent>
                                            </DetailsPanel>
                                        </Grid>
                                        <Grid
                                            size={{
                                                xs: 12,
                                                lg: 6,
                                            }}
                                            sx={{
                                                display: "flex",
                                                alignItems: "stretch",
                                            }}
                                        >
                                            <DetailsPanel>
                                                <DetailsHeader>Lot Properties</DetailsHeader>
                                                <DetailsContent>
                                                    <GridColumn>
                                                        {lotData?.properties?.map((property, index) => (
                                                            <GridRow>
                                                                <GridLabel
                                                                    sx={{
                                                                        paddingTop: theme.spacing(1),
                                                                    }}
                                                                >
                                                                    {property.name}
                                                                </GridLabel>
                                                                <GridData
                                                                    sx={{
                                                                        marginTop: theme.spacing(1),
                                                                        marginBottom:
                                                                            index + 1 == lotData?.properties?.length
                                                                                ? theme.spacing(1)
                                                                                : 0,
                                                                        display: "flex",
                                                                        width: "100%",
                                                                        alignItems: "flex-start",
                                                                        justifyContent: "space-between",
                                                                    }}
                                                                >
                                                                    {property.value ?? <Box />}
                                                                    {permissions?.includes(
                                                                        Permissions.LotCanEditProperty,
                                                                    ) && (
                                                                        <Box
                                                                            sx={{
                                                                                paddingLeft: 2,
                                                                                paddingRight: 2,
                                                                            }}
                                                                        >
                                                                            <StyledActionButton
                                                                                variant="edit"
                                                                                to={`/projects/${projectId}/stages/${stageId}/lots/${lotId}/properties/edit/${property.id}`}
                                                                            />
                                                                        </Box>
                                                                    )}
                                                                </GridData>
                                                            </GridRow>
                                                        ))}
                                                        <GridFillerRow />
                                                    </GridColumn>
                                                </DetailsContent>
                                            </DetailsPanel>
                                        </Grid>
                                    </Grid>

                                    <Box
                                        sx={{
                                            marginTop: theme.spacing(2),
                                            display: "flex",
                                            flexDirection: "column",
                                        }}
                                    >
                                        <CollapsibleAppTable
                                            title="Contracts"
                                            loading={contractSummariesLoading}
                                            rows={contractSummaries ?? []}
                                            columns={contractColumns}
                                            getRowId={requiredContractId}
                                            density="compact"
                                            startCollapsed={false}
                                            columnControls={{
                                                menu: true,
                                            }}
                                            filterControls
                                            export={contractsExport}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            marginTop: theme.spacing(2),
                                        }}
                                    >
                                        <CollapsibleAppTable
                                            title="Notes"
                                            loading={lotNotesLoading}
                                            rows={lotNotes ?? []}
                                            columns={lotNoteColumns}
                                            getRowId={requiredLotNoteId}
                                            density="compact"
                                            startCollapsed={false}
                                            columnControls={{
                                                menu: true,
                                            }}
                                            filterControls
                                            export={notesExport}
                                        />
                                    </Box>
                                    <Paper>
                                        <List disablePadding>
                                            <LotHistory lotId={lotId} />
                                        </List>
                                    </Paper>
                                    <LotAttachments lotId={lotId} />
                                </ContractSummariesContext.Provider>
                            </LotAttachmentsContext.Provider>
                        </LotNotesContext.Provider>
                    </LotPropertiesContext.Provider>
                </LotChangesContext.Provider>
            </LotDetailsContext.Provider>
        </>
    );
};
